import { forwardRef, useImperativeHandle, useState } from "react";
import { Stack } from "@mui/material";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import LinkItem from "src/Pages/Company/Dashboard/pages/Widgets/MainForm/LinkItem";
import { UnformattedWidgetLink } from "./MainFormLinkForm.models";

export interface ReorderLinksFormRef {
  getOrderedLinks: () => UnformattedWidgetLink[];
}

interface ReorderableLink extends UnformattedWidgetLink {
  id: string;
}

interface ReorderLinksFormProps {
  links: ReorderableLink[];
}

const ReorderLinksForm = forwardRef<ReorderLinksFormRef, ReorderLinksFormProps>(
  ({ links }, ref) => {
    const [orderedLinks, setOrderedLinks] = useState<ReorderableLink[]>(links);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (active && over && active.id !== over.id) {
        setOrderedLinks((prev) => {
          const oldIndex = prev.findIndex((link) => link.id === active.id);
          const newIndex = prev.findIndex((link) => link.id === over.id);

          if (oldIndex === -1 || newIndex === -1) {
            return prev;
          }

          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    };

    useImperativeHandle(ref, () => ({
      getOrderedLinks: () => orderedLinks.map(({ id: _id, ...link }) => link),
    }));

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement, restrictToWindowEdges]}
      >
        <SortableContext
          items={orderedLinks.map((link) => link.id)}
          strategy={rectSortingStrategy}
        >
          <Stack
            useFlexGap
            spacing="2rem"
            sx={{
              margin: "1rem 0 0 0",
              flexWrap: "wrap",
              flexDirection: "row",
              // The global modal applies `& .MuiPaper-root { width: 100% }`,
              // which also matches the default-icon Paper and makes iconless
              // links wider than the ones with icons. Reset it to the 72px
              // tile used in the main form.
              "& .MuiPaper-root": {
                width: "72px",
                maxWidth: "72px",
                height: "72px",
                padding: 0,
              },
            }}
            data-testid="reorder-links-form"
          >
            {orderedLinks.map((link) => (
              <LinkItem
                key={link.id}
                id={link.id}
                name={link.name}
                icon={link.icon}
                draggable
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    );
  }
);

ReorderLinksForm.displayName = "ReorderLinksForm";

export default ReorderLinksForm;
